import clientApi from './clientApi';

export const clientPortalService = {
  async getDashboard() {
    const response = await clientApi.get('/client-portal/dashboard');
    return response.data;
  },

  async getProducts(filters = {}) {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.search) params.append('search', filters.search);
    if (filters.is_seasonal !== undefined) params.append('is_seasonal', filters.is_seasonal);
    if (filters.delivery_date) params.append('delivery_date', filters.delivery_date);

    const response = await clientApi.get(`/client-portal/products?${params}`);
    return response.data;
  },

  async getDeliveryCalendar(weeks = 8) {
    const response = await clientApi.get(`/client-portal/delivery-calendar?weeks=${weeks}`);
    return response.data;
  },

  async createOrder(orderData) {
    const response = await clientApi.post('/client-portal/orders', orderData);
    return response.data;
  },

  async getOrders(status = null) {
    const params = status ? { status } : {};
    const response = await clientApi.get('/client-portal/orders', { params });
    return response.data;
  },

  async getProfile() {
    const response = await clientApi.get('/client-portal/profile');
    return response.data;
  },

  async getInvoices(status = null) {
    const params = status ? { status } : {};
    const response = await clientApi.get('/client-portal/invoices', { params });
    return response.data;
  },

  async getMessages(status = null) {
    const params = status ? { status } : {};
    const response = await clientApi.get('/client-portal/messages', { params });
    return response.data;
  },

  async getMessagesSummary() {
    const response = await clientApi.get('/client-portal/messages/summary');
    return response.data;
  },

  async markMessageRead(messageId) {
    const response = await clientApi.post(`/client-portal/messages/${messageId}/read`);
    return response.data;
  },

  async updateMessageStatus(messageId, status) {
    const response = await clientApi.patch(`/client-portal/messages/${messageId}`, { status });
    return response.data;
  },

  async updateNotificationPreferences(preferences) {
    const response = await clientApi.put('/client-portal/notification-preferences', preferences);
    return response.data;
  },

  // Order editing
  async updateOrder(orderId, orderData) {
    const response = await clientApi.put(`/client-portal/orders/${orderId}`, orderData);
    return response.data;
  },

  // Order cancellation
  async cancelOrder(orderId) {
    const response = await clientApi.post(`/client-portal/orders/${orderId}/cancel`);
    return response.data;
  },

  // Change requests (advance-invoiced orders need bakery approval)
  async createOrderChangeRequest(orderId, requestedItems, reason) {
    const response = await clientApi.post(`/client-portal/orders/${orderId}/change-requests`, {
      requested_items: requestedItems,
      reason,
    });
    return response.data;
  },

  async getOrderChangeRequests(orderId) {
    const response = await clientApi.get(`/client-portal/orders/${orderId}/change-requests`);
    return response.data;
  },

  // Payment methods
  async createInvoiceCheckout(invoiceId) {
    const response = await clientApi.post('/payments/invoice/checkout', {
      invoice_id: invoiceId,
      origin_url: window.location.origin
    });
    return response.data;
  },

  async getPaymentStatus(sessionId) {
    const response = await clientApi.get(`/payments/status/${sessionId}`);
    return response.data;
  },

  // Invoice PDF download
  async downloadInvoicePDF(invoiceId) {
    const response = await clientApi.get(`/invoices/${invoiceId}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Phase 2: Invoice adjustment workflow
  async getInvoicesV2(status = null) {
    const params = status ? { status_filter: status } : {};
    const response = await clientApi.get('/invoices/client', { params });
    return response.data;
  },

  async getInvoiceDetail(invoiceId) {
    const response = await clientApi.get(`/invoices/client/${invoiceId}`);
    return response.data;
  },

  async submitAdjustment(invoiceId, adjustmentData) {
    const response = await clientApi.post(`/invoices/client/${invoiceId}/adjustment`, adjustmentData);
    return response.data;
  },

  // Payment Methods (Phase 4)
  async getPaymentMethods() {
    const response = await clientApi.get('/payments/methods');
    return response.data;
  },

  async createSetupIntent() {
    const response = await clientApi.post('/payments/methods/setup', {
      origin_url: window.location.origin
    });
    return response.data;
  },

  async setDefaultPaymentMethod(paymentMethodId) {
    const response = await clientApi.post(`/payments/methods/${paymentMethodId}/default`);
    return response.data;
  },

  async deletePaymentMethod(paymentMethodId) {
    const response = await clientApi.delete(`/payments/methods/${paymentMethodId}`);
    return response.data;
  },

  async payWithSavedCard(invoiceId, paymentMethodId) {
    const response = await clientApi.post('/payments/pay-with-saved', {
      invoice_id: invoiceId,
      payment_method_id: paymentMethodId
    });
    return response.data;
  },

  // ACH Bank Account Methods
  async getAllPaymentMethods() {
    const response = await clientApi.get('/payments/methods/all');
    return response.data;
  },

  async createBankSetupIntent() {
    const response = await clientApi.post('/payments/methods/bank/setup', {
      origin_url: window.location.origin
    });
    return response.data;
  },

  async payWithBankAccount(invoiceId, paymentMethodId) {
    const response = await clientApi.post('/payments/pay-with-bank', {
      invoice_id: invoiceId,
      payment_method_id: paymentMethodId
    });
    return response.data;
  },

  async checkAchPaymentStatus(paymentIntentId) {
    const response = await clientApi.get(`/payments/ach/status/${paymentIntentId}`);
    return response.data;
  },

  // Onboarding (guided tour v2)
  async getOnboardingStatus() {
    const response = await clientApi.get('/client-portal/onboarding/status');
    return response.data;
  },

  async completeOnboarding(version) {
    const response = await clientApi.post('/client-portal/onboarding/complete', { version });
    return response.data;
  },

  async dismissOnboarding(version) {
    const response = await clientApi.post('/client-portal/onboarding/dismiss', { version });
    return response.data;
  },

  async replayOnboarding() {
    const response = await clientApi.post('/client-portal/onboarding/replay');
    return response.data;
  },

  async getMyPermissions() {
    const response = await clientApi.get('/client-portal/permissions');
    return response.data;
  },

  // Help Center (client read)
  async getHelpCategories() {
    const response = await clientApi.get('/help/categories');
    return response.data;
  },

  async getHelpArticles(params = {}) {
    const response = await clientApi.get('/help/articles', { params });
    return response.data;
  },

  async getHelpArticle(slug) {
    const response = await clientApi.get(`/help/articles/${slug}`);
    return response.data;
  },

  async submitHelpFeedback(articleId, helpful) {
    const response = await clientApi.post(`/help/articles/${articleId}/feedback`, { helpful });
    return response.data;
  },

  // Documents
  async getMyDocuments() {
    const response = await clientApi.get('/documents/my-documents');
    return response.data;
  },

  async getPendingDocumentsCount() {
    const response = await clientApi.get('/documents/my-documents/pending-count');
    return response.data;
  },

  async getMyDocument(id) {
    const response = await clientApi.get(`/documents/my-documents/${id}`);
    return response.data;
  },

  async acknowledgeDocument(id) {
    const response = await clientApi.post(`/documents/my-documents/${id}/acknowledge`);
    return response.data;
  },

  // Menu announcement contacts (bare emails, no portal login)
  async getAnnouncementContacts() {
    const response = await clientApi.get('/client-portal/announcement-contacts');
    return response.data;
  },

  async addAnnouncementContact(payload) {
    const response = await clientApi.post('/client-portal/announcement-contacts', payload);
    return response.data;
  },

  async updateAnnouncementContact(contactId, payload) {
    const response = await clientApi.patch(
      `/client-portal/announcement-contacts/${contactId}`, payload,
    );
    return response.data;
  },

  async deleteAnnouncementContact(contactId) {
    const response = await clientApi.delete(`/client-portal/announcement-contacts/${contactId}`);
    return response.data;
  },

  // Order Insights (suggested quantities)
  async getOrderInsights(deliveryDate) {
    const response = await clientApi.get('/client-portal/order-insights', {
      params: { delivery_date: deliveryDate },
    });
    return response.data;
  },

  async getProductHistory(productId, deliveryDate) {
    const response = await clientApi.get('/client-portal/order-insights/history', {
      params: { product_id: productId, delivery_date: deliveryDate },
    });
    return response.data;
  },

  // Over-ordering: the client's own sold-against-ordered breakdown
  async getSalesPerformance(weeks = 12) {
    const response = await clientApi.get('/client-portal/reports/sales-performance', {
      params: { weeks },
    });
    return response.data;
  },
};
